import { IItem, ITotals } from '@checkout/items';
import { IPaths, IPaymentMethod, RootState } from '@checkout/state';
import * as cx from 'classnames';
import * as React from 'react';
import { Fragment } from 'react';
import { InjectedIntlProps, injectIntl } from 'react-intl';
import { connect } from 'react-redux';
import { compose } from 'redux';
import { Overlay, ProductsList } from '..';
import { CheckoutContextConfig } from '../../../apps/CheckoutApp';
import { walletMethods } from '../../../config/contextCheckoutConfig';
import { toggleCartVisibility } from '../../../reducers/checkout';
import { isKlarnaCheckoutAvailable } from '../../../utils/paymentMethods';
import { Button, EmptyBagInfo, OrderSection, PaymentLogosBar,  SectionHeader, Summary } from '../../presentational';
import messages from './../../../i18n/base-en';
import * as Styles from './Cart.scss';

interface IOwnProps {}

interface IConnectedProps {
  items: IItem[];
  totals: ITotals;
  isCartOpen: boolean;
  pathnames: IPaths;
  paymentMethods: IPaymentMethod[];
}

interface IConnectetDispatchProps {
  toggleCartVisibility: typeof toggleCartVisibility;
}

type Props = IOwnProps & IConnectedProps & IConnectetDispatchProps & InjectedIntlProps;

interface IOwnState {
  isOverflowed: boolean;
}

class Cart extends React.Component<Props, IOwnState> {

  state: IOwnState = {
    isOverflowed: false,
  };

  private mainElement: HTMLDivElement;
  private cartElement: HTMLDivElement;

  componentWillMount() {
    window.addEventListener('resize', this.checkIfCartIsOverflowed, false);
    document.addEventListener('mousedown', this.checkIfClickedOutsideCart, false);
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.checkIfCartIsOverflowed, false);
    document.removeEventListener('mousedown', this.checkIfClickedOutsideCart, false);
  }

  componentWillUpdate() {
    this.checkIfCartIsOverflowed();
  }

  componentWillReceiveProps(nextProps: Props) {
    if (nextProps.isCartOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'auto';
    }
  }

  checkIfClickedOutsideCart = ({ target }: any) => {
    const { isCartOpen } = this.props;
    const cartButton: Element | null = document.querySelector('[data-ignore-click-outside]');

    // Ignore when user click on `data-ignore-click-outside` element
    if (cartButton !== null && cartButton.contains(target)) {
      return;
    }

    if (!this.cartElement.contains(target) && isCartOpen) {
      this.toggleVisibility();
    }
  }

  checkIfCartIsOverflowed = () => {
    const mainElement = this.mainElement;
    const isOverflowed = mainElement && mainElement.offsetHeight < mainElement.scrollHeight;

    if (this.state.isOverflowed !== isOverflowed) {
      // this.setState({ isOverflowed }); // TODO - temporary turn this off because problems on IE11
    }
  }

  gotToCheckout = () => {
    window.location.href = this.props.pathnames.checkout;
  }

  gotToWallet = () => {
    window.location.href = this.props.pathnames.wallet;
  }

  toggleVisibility = () => {
    this.props.toggleCartVisibility();
  }

  render() {
    const { isOverflowed } = this.state;
    const { items, totals, intl, isCartOpen, paymentMethods } = this.props;
    const { formatMessage } = intl;
    const noItemsInBag = !items || (items && items.length === 0);

    const cartClassNames = cx({
      [Styles.cart]: true,
      [Styles.open]: isCartOpen,
      [Styles.overflowed]: isOverflowed,
    });

    return (
      <CheckoutContextConfig.Consumer>
        {(checkoutContextConfig => (
          <Fragment>
            {isCartOpen && <div className={Styles.overlay} />}
            <div className={cartClassNames}  ref={ cartElement => this.cartElement = cartElement as HTMLDivElement }>
              <Overlay />
              <div className={Styles.inner}>
                <header className={Styles.header}>
                  <SectionHeader title={formatMessage(messages.cart_label)} />
                  <Button
                    align="center"
                    label={formatMessage(messages.close_cart_button_label)}
                    variant="clear"
                    size="small"
                    type="button"
                    onClick={this.toggleVisibility}
                  />
                </header>
                <div className={Styles.main} ref={ mainElement => this.mainElement = mainElement as HTMLDivElement }>
                  <OrderSection borderBottom={(!isOverflowed && items !== undefined && items.length > 0)} blockFor="CartProductsList">
                    {!noItemsInBag && (<ProductsList items={items} />)}
                    {noItemsInBag && <EmptyBagInfo intl={intl} withLink={false} />}
                  </OrderSection>
                </div>
                <footer className={Styles.footer}>
                  {!noItemsInBag && (
                    <Fragment>
                      <OrderSection
                        blockFor="CartSummary"
                        borderBottom={checkoutContextConfig.ui.cartFooterNote.enabled && checkoutContextConfig.ui.cartFooterNote.html !== ''}
                      >
                        <Summary totals={totals} />
                      </OrderSection>
                      {checkoutContextConfig.ui.cartFooterNote.enabled &&
                        checkoutContextConfig.ui.cartFooterNote.html !== '' && (
                        <OrderSection blockFor="CartFooterNote" borderBottom={false}>
                          <div className={Styles.note} dangerouslySetInnerHTML={{ __html: checkoutContextConfig.ui.cartFooterNote.html }} />
                        </OrderSection>
                      )}
                      <OrderSection borderBottom={false}>
                        {checkoutContextConfig.modules.wallet.isEnabled &&
                          checkoutContextConfig.modules.wallet.method === walletMethods.klarnaCheckout &&
                        isKlarnaCheckoutAvailable(paymentMethods) && (
                          <Fragment>
                            <Button
                              align="center"
                              label={formatMessage(messages.cart_wallet_klarna_checkout_label)}
                              variant="outline"
                              type="button"
                              onClick={this.gotToWallet}
                            />
                            <div className={Styles.separator}>
                              {formatMessage(messages.cart_separator_text)}
                            </div>
                          </Fragment>
                        )}
                        <Button
                          align="center"
                          label={formatMessage(messages.checkout_button_label)}
                          variant="primary"
                          type="button"
                          onClick={this.gotToCheckout}
                        />
                        {checkoutContextConfig.ui.cartPaymentLogoBar.enabled && (
                          <PaymentLogosBar paymentMethods={paymentMethods} />
                        )}
                      </OrderSection>
                    </Fragment>
                  )}
                </footer>
              </div>
            </div>
          </Fragment>
        ))}
      </CheckoutContextConfig.Consumer>
    );
  }
}

export default compose<React.ComponentClass>(
  injectIntl,
  connect<IConnectedProps,  IConnectetDispatchProps>(
    (state: RootState) => ({
      items:  state.checkout.selection.items,
      totals: state.checkout.selection.totals,
      isCartOpen: state.checkout.ui.cart.isOpen,
      pathnames: state.checkout.pathnames,
      paymentMethods: state.checkout.paymentMethods,
    }),
    {
      toggleCartVisibility,
    }
  ),
)(Cart);



// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/components/container/Cart/Cart.tsx